import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ProductDialog from './ProductDialog';
import {
    abandonedCartDetail,
    abandonedCartList
} from '../../../actions/admin/cart';

import {
    Alert, Box, Button, CircularProgress, IconButton, Paper,
    Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TablePagination, TableRow, TextField, Tooltip,
    Typography
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

const INITIAL_FILTERS = {
    search: '',
    dateFrom: '',
    dateTo: ''
};

const INITIAL_PAGINATION = {
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0
};

const currency = new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN'
});

const AbandonedCart = () => {
    const dispatch = useDispatch();
    const [carts, setCarts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [pagination, setPagination] = useState(INITIAL_PAGINATION);
    const [open, setOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedCart, setSelectedCart] = useState(null);

    const loadCarts = async (nextFilters, page = 1, pageSize = pagination.pageSize) => {
        setLoading(true);
        await abandonedCartList(
            { ...nextFilters, page, pageSize },
            setCarts, setPagination, setLoading, dispatch
        );
    };

    useEffect(() => {
        loadCarts(INITIAL_FILTERS);
    }, []);

    const handleFilterChange = ({ target: { name, value } }) => {
        setFilters(current => ({ ...current, [name]: value }));
    };

    const applyFilters = () => {
        loadCarts(filters, 1);
    };

    const clearFilters = () => {
        setFilters(INITIAL_FILTERS);
        loadCarts(INITIAL_FILTERS, 1);
    };

    const openCartDetail = async id => {
        setOpen(true);
        setDetailLoading(true);
        setSelectedCart(null);
        const cart = await abandonedCartDetail(id, dispatch);
        setSelectedCart(cart);
        setDetailLoading(false);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h5">Carritos abandonados</Typography>
                <Tooltip title="Carritos abiertos que recibieron un recordatorio y no se convirtieron en pedido">
                    <IconButton aria-label="información de carritos abandonados">
                        <InfoIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Actualizar resultados">
                    <IconButton
                        aria-label="actualizar resultados"
                        onClick={() => loadCarts(filters, pagination.page)}
                        disabled={loading}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
                    <TextField
                        size="small"
                        name="search"
                        label="Cliente o correo"
                        value={filters.search}
                        onChange={handleFilterChange}
                        onKeyDown={event => {
                            if (event.key === 'Enter') applyFilters();
                        }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        name="dateFrom"
                        label="Última actividad desde"
                        value={filters.dateFrom}
                        onChange={handleFilterChange}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        name="dateTo"
                        label="Última actividad hasta"
                        value={filters.dateTo}
                        onChange={handleFilterChange}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon />}
                        onClick={applyFilters}
                        disabled={loading}
                    >
                        Buscar
                    </Button>
                    <Button variant="outlined" onClick={clearFilters} disabled={loading}>
                        Limpiar
                    </Button>
                </Stack>
            </Paper>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Cliente</TableCell>
                            <TableCell>Correo</TableCell>
                            <TableCell align="right">Productos</TableCell>
                            <TableCell align="right">Total estimado</TableCell>
                            <TableCell>Se creó</TableCell>
                            <TableCell>Última actividad</TableCell>
                            <TableCell>Recordatorio</TableCell>
                            <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : carts.length > 0 ? carts.map(cart => (
                            <TableRow key={cart._id} hover>
                                <TableCell>{cart.fullname}</TableCell>
                                <TableCell>{cart.email}</TableCell>
                                <TableCell align="right">{cart.item_count}</TableCell>
                                <TableCell align="right">{currency.format(cart.estimated_total)}</TableCell>
                                <TableCell>{cart.created}</TableCell>
                                <TableCell>{cart.updated}</TableCell>
                                <TableCell>{cart.reminder_date}</TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Ver detalle">
                                        <IconButton
                                            aria-label="ver detalle del carrito"
                                            onClick={() => openCartDetail(cart._id)}
                                        >
                                            <RemoveRedEyeIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                    <Alert severity="info">No hay carritos que coincidan con los filtros.</Alert>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={Math.max(0, pagination.page - 1)}
                    onPageChange={(_, nextPage) => loadCarts(filters, nextPage + 1)}
                    rowsPerPage={pagination.pageSize}
                    onRowsPerPageChange={({ target: { value } }) =>
                        loadCarts(filters, 1, Number(value))
                    }
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </TableContainer>

            <ProductDialog
                open={open}
                setOpen={setOpen}
                cart={selectedCart}
                loading={detailLoading}
            />
        </Box>
    );
};

export default AbandonedCart;
